import { FileText, AlertTriangle } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex p-4 rounded-2xl bg-purple-500/10 border border-purple-500/20 mb-6">
            <FileText className="w-12 h-12 text-purple-400" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Terms of Service
          </h1>
          <p className="text-gray-400 text-lg">
            Last Updated: February 23, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-3xl p-8 lg:p-12">
            
            {/* Notice Box */}
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-6 mb-12">
              <p className="text-gray-300 leading-relaxed">
                Please read these Terms of Service carefully before using PulseNex services.
                By accessing or using our services, you agree to be bound by these terms.
              </p>
            </div>

            <div className="space-y-12 text-gray-400">
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">1</span>
                  Acceptance of Terms
                </h2>
                <p className="leading-relaxed">
                  By accessing and using PulseNex services, you accept and agree to be bound by the terms
                  and provision of this agreement. If you do not agree to these terms, please do not use
                  our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">2</span>
                  Use of Services
                </h2>
                <p className="mb-4 leading-relaxed">Our services are intended for use by healthcare professionals and organizations. You agree to:</p>
                <ul className="space-y-3">
                  {[
                    'Provide accurate and complete information',
                    'Maintain the security of your account credentials',
                    'Use the services in compliance with all applicable laws and regulations',
                    'Not use the services for any unlawful or prohibited purpose',
                    'Not attempt to gain unauthorized access to our systems'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">3</span>
                  Medical Disclaimer
                </h2>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-yellow-200/80 text-sm leading-relaxed">
                      PulseNex AI solutions are designed to assist healthcare professionals and should not be
                      used as a substitute for professional medical judgment.
                    </p>
                  </div>
                </div>
                <p className="mb-4 leading-relaxed">Our services:</p>
                <ul className="space-y-3">
                  {[
                    'Are intended to supplement, not replace, clinical decision-making',
                    'Should be used in conjunction with professional medical expertise',
                    'Do not constitute medical advice, diagnosis, or treatment',
                    'Require validation by qualified healthcare professionals'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">4</span>
                  Intellectual Property
                </h2>
                <p className="leading-relaxed">
                  All content, features, and functionality of PulseNex services, including but not limited
                  to software, algorithms, text, graphics, logos, and data, are owned by PulseNex and are
                  protected by United States and international copyright, trademark, and other intellectual
                  property laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">5</span>
                  User Data and Privacy
                </h2>
                <p className="leading-relaxed">
                  Your use of our services is also governed by our <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>. We are committed to
                  protecting your data and maintaining HIPAA compliance for all protected health information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">6</span>
                  Service Availability
                </h2>
                <p className="mb-4 leading-relaxed">We strive to provide reliable service but do not guarantee that:</p>
                <ul className="space-y-3">
                  {[
                    'Services will be uninterrupted or error-free',
                    'Defects will be corrected immediately',
                    'Services will meet your specific requirements',
                    'Results obtained from using the services will be accurate or reliable'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">7</span>
                  Limitation of Liability
                </h2>
                <p className="leading-relaxed">
                  To the maximum extent permitted by law, PulseNex shall not be liable for any indirect,
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
                  whether incurred directly or indirectly, or any loss of data, use, goodwill, or other
                  intangible losses.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">8</span>
                  Indemnification
                </h2>
                <p className="leading-relaxed">
                  You agree to indemnify and hold harmless PulseNex and its affiliates, officers, agents,
                  and employees from any claim or demand, including reasonable attorneys' fees, made by
                  any third party due to or arising out of your use of the services, your violation of
                  these Terms, or your violation of any rights of another.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">9</span>
                  Termination
                </h2>
                <p className="leading-relaxed">
                  We reserve the right to terminate or suspend your account and access to services
                  immediately, without prior notice or liability, for any reason, including breach of
                  these Terms. Upon termination, your right to use the services will immediately cease.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">10</span>
                  Governing Law
                </h2>
                <p className="leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the
                  State of Illinois, United States, without regard to its conflict of law provisions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">11</span>
                  Contact Information
                </h2>
                <p className="mb-6 leading-relaxed">If you have questions about these Terms, please contact us:</p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-white font-semibold mb-3">PulseNex Legal Team</p>
                  <div className="space-y-2 text-gray-400">
                    <p>Email: <a href="mailto:legal@pulsnex.com" className="text-cyan-400 hover:underline">legal@pulsnex.com</a></p>
                    <p>Phone: <a href="tel:6302378000" className="text-cyan-400 hover:underline">(630) 237-8000</a></p>
                    <p>Address: 111 N Wabash Ave, Ste 100, Chicago, IL 60602</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
